<template>
  <div
    class="twilio-pip-dialer flex h-full flex-col bg-grey-navigation text-white"
  >
    <!-- Phone Number Header -->
    <div
      v-if="activeUserPhoneNumber && hasActiveCall"
      class="flex items-center justify-between border-b border-white/10 p-4"
    >
      <div>
        {{
          isOutgoing
            ? $t("DIALER.OUTGOING_CALL_FROM")
            : $t("DIALER.INCOMING_CALL_TO")
        }}:
      </div>
      <div
        v-tooltip="$t('COMMON.COPY_TO_CLIPBOARD')"
        class="cursor-pointer"
        @click.stop.prevent="
          copyToClipboard(activeUserPhoneNumber, { isPiP: true })
        "
      >
        {{ activeUserPhoneNumber }}
      </div>
    </div>

    <!-- Status Header (shown when no active call) -->
    <div
      v-else
      class="flex items-center justify-between border-b border-white/10 p-4"
    >
      <span class="font-semibold">{{ $t("DIALER.TITLE") }}</span>
      <div
        v-if="deviceStatus"
        class="flex items-center space-x-2 text-xs"
        :class="statusClass"
      >
        <span class="size-2 rounded-full" :class="statusDotClass" />
        <span>{{ deviceStatus }}</span>
      </div>
    </div>

    <!-- Call Info -->
    <div v-if="hasActiveCall" class="flex-1">
      <div
        class="flex w-full items-center space-x-3 border-b border-white/10 p-4 text-white"
      >
        <lf-avatar :username="contactName || $t('COMMON.UNKNOWN')" :size="40" />
        <div class="flex min-w-0 flex-col">
          <span v-tooltipable.themeLight class="truncate font-semibold">
            {{ contactName || $t("COMMON.UNKNOWN") }}
          </span>
          <span data-cy="to-number" class="text-xs text-label">
            {{ contactNumber || "-" }}
          </span>
        </div>
      </div>
      <div class="call-details flex w-full flex-col space-y-1 p-4">
        <div
          v-for="row in infoRows"
          :key="row.name"
          :data-cy="row.name"
          class="call-session-info-row group"
        >
          <icon-base
            width="12"
            height="12"
            :view-box="row.iconOptions.viewBox"
            :icon="row.iconOptions.icon"
          />
          <span v-tooltipable.themeLight class="truncate">
            {{ row.value }}
          </span>
          <a
            v-if="row.link"
            :href="getRouteHref(row.link)"
            target="_opener"
            class="cursor-pointer"
          >
            <icon-base
              width="12"
              height="12"
              view-box="0 0 24 24"
              :icon="IconLink"
            />
          </a>
        </div>
      </div>
    </div>

    <!-- Idle State -->
    <div
      v-else
      class="flex flex-1 flex-col items-center justify-center p-4 text-center"
    >
      <icon-base
        :icon="IconDialerPhone"
        width="48"
        height="48"
        class="mb-4 text-label"
      />
      <p class="text-sm text-label">
        {{ $t("DIALER.WAITING_FOR_CALLS") }}
      </p>
      <p class="mt-2 text-xs text-label/60">
        {{ $t("DIALER.PIP_ACTIVE") }}
      </p>
    </div>

    <!-- Incoming Call Actions -->
    <div v-if="callStore.hasIncomingCall" class="twilio-dialer-actions">
      <div
        :title="$t('DIALER.IGNORE')"
        class="dialer-round-button flex-none bg-white/10"
        @click="toggleSilence"
      >
        <icon-base :icon="isRingtoneSilenced ? IconSpeakerMute : IconSpeaker" />
      </div>
      <lf-button
        type="error"
        class="space-x-2 bg-error"
        centered
        @click="rejectCall"
      >
        <template #icon>
          <icon-base :icon="IconDialerPhone" class="rotate-135" />
        </template>
        <span>{{ $t("DIALER.DECLINE") }}</span>
      </lf-button>
      <lf-button
        type="success"
        class="space-x-2 bg-success"
        centered
        @click="acceptCall"
      >
        <template #icon>
          <icon-base :icon="IconDialerPhone" />
        </template>
        <span>{{ $t("DIALER.ACCEPT") }}</span>
      </lf-button>
    </div>

    <!-- Ongoing Call Actions -->
    <div v-else-if="callStore.hasOngoingCall" class="twilio-dialer-actions">
      <div
        :title="
          callStore.isMicrophoneMuted ? $t('DIALER.UNMUTE') : $t('DIALER.MUTE')
        "
        class="flex size-12 cursor-pointer items-center justify-center rounded-full bg-white/10"
        @click="toggleMute"
      >
        <icon-base
          :icon="
            callStore.isMicrophoneMuted ? IconMicrophoneMute : IconMicrophone
          "
        />
      </div>
      <div
        :title="$t('COMMON.DISCONNECT')"
        class="flex size-12 cursor-pointer items-center justify-center rounded-full bg-error"
        @click="disconnectCall"
      >
        <icon-base :icon="IconDialerPhone" class="rotate-135" />
      </div>
    </div>

    <!-- Device Selection -->
    <template v-if="callStore.device && isOutgoing">
      <twilio-device-select
        :devices="groupedDevices.input"
        :selected-device="callStore.device?.audio?.inputDevice?.deviceId"
        type="input"
        @device:select="setDevice"
        @device:refresh="authorizeUseMedia"
      />
      <twilio-device-select
        v-if="callStore.device?.audio?.isOutputSelectionSupported"
        :devices="groupedDevices.output"
        :selected-device="selectedOutputDevice"
        type="output"
        @device:select="setDevice"
        @device:refresh="authorizeUseMedia"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, toRaw } from "vue";
import { useI18n } from "vue-i18n";
import { type RouteLocationRaw, useRouter } from "vue-router";

import * as Sentry from "@sentry/vue";
import { storeToRefs } from "pinia";

import {
  CallSessionStatus,
  CommunicationType,
  PhoneSelectionType,
} from "@/enums/communicationLogs";
import {
  TwilioCallActions,
  TwilioCallDirection,
  TwilioDeviceState,
} from "@/enums/communicationProviders";
import { useClipboardWithMessage } from "@/hooks/clipboard";
import { useTwilio } from "@/hooks/communicationProviders";
import { useNotification } from "@/hooks/notifications";
import { ROUTE_DEAL_PROGRESS } from "@/router/routes";
import { useCallsStore } from "@/stores/calls";

import IconDialerPhone from "@/components/icons/IconDialerPhone.vue";
import IconLink from "@/components/icons/IconLink.vue";
import IconMicrophone from "@/components/icons/IconMicrophone.vue";
import IconMicrophoneMute from "@/components/icons/IconMicrophoneMute.vue";
import IconPersons from "@/components/icons/IconPersons.vue";
import IconProfileSmall from "@/components/icons/IconProfileSmall.vue";
import IconSpeaker from "@/components/icons/IconSpeaker.vue";
import IconSpeakerMute from "@/components/icons/IconSpeakerMute.vue";
import IconDeals from "@/components/icons/sidebar/IconDeals.vue";
import TwilioDeviceSelect from "@/components/twilio/TwilioDeviceSelect.vue";
import IconBase from "@/components/ui/IconBase.vue";
import LfAvatar from "@/components/ui/LFAvatar.vue";
import LfButton from "@/components/ui/buttons/LfButton.vue";

const { t } = useI18n();
const router = useRouter();

const callStore = useCallsStore();
const { callDialog } = storeToRefs(callStore);
const { savedPhoneNumber } = useTwilio();
const { showMessage } = useNotification();
const copyToClipboard = useClipboardWithMessage();

const isRingtoneSilenced = ref(false);
const audioDevices = ref<MediaDeviceInfo[] | null>(null);

const hasActiveCall = computed(
  () => callStore.isCallActive || callStore.isDialing
);

const groupedDevices = computed(() => {
  const empty: { input: MediaDeviceInfo[]; output: MediaDeviceInfo[] } = {
    input: [],
    output: [],
  };
  if (!audioDevices.value) {
    return empty;
  }
  return audioDevices.value.reduce((acc, curr) => {
    const type =
      curr.kind === "audioinput"
        ? "input"
        : curr.kind === "audiooutput"
          ? "output"
          : null;
    if (type) {
      acc[type].push(curr);
    }
    return acc;
  }, empty);
});

const selectedOutputDevice = computed(
  () =>
    [...(callStore.device?.audio?.speakerDevices?.get() ?? new Set())]?.[0]
      ?.deviceId ?? ""
);

const deviceStatus = computed(() => {
  const stateMap = {
    [TwilioDeviceState.Idle]: t("DIALER.READY"),
    [TwilioDeviceState.Incoming]: t("DIALER.INCOMING_CALL_TO"),
    [TwilioDeviceState.Ongoing]: t("DIALER.ONGOING_CALL"),
    [TwilioDeviceState.Ringing]: t("DIALER.RINGING"),
  };
  return stateMap[callStore.deviceState] ?? null;
});

const statusClass = computed(() => ({
  "text-success":
    callStore.deviceState === TwilioDeviceState.Idle && callStore.device,
  "text-warning": callStore.deviceState === TwilioDeviceState.Incoming,
  "text-primary": callStore.deviceState === TwilioDeviceState.Ongoing,
}));

const statusDotClass = computed(() => ({
  "bg-success":
    callStore.deviceState === TwilioDeviceState.Idle && callStore.device,
  "bg-warning animate-pulse":
    callStore.deviceState === TwilioDeviceState.Incoming,
  "bg-primary": callStore.deviceState === TwilioDeviceState.Ongoing,
}));

const isOutgoing = computed(
  () => callStore.callDirection === TwilioCallDirection.Outgoing
);

const contactName = computed(() => {
  if (isOutgoing.value) {
    return callStore.activeCallSession?.data?.callee?.name ?? null;
  }
  const call = callStore.currentCall;
  const rawCall = call ? toRaw(call) : null;
  return rawCall?.customParameters?.get("callerFullName") ?? null;
});

const contactNumber = computed(() => {
  if (isOutgoing.value) {
    return callStore.activeCallSession?.to ?? null;
  }
  const call = callStore.currentCall;
  const rawCall = call ? toRaw(call) : null;
  return rawCall?.parameters?.From ?? rawCall?.parameters?.To ?? null;
});

const businessName = computed(() => {
  if (isOutgoing.value) {
    return (
      callStore.activeCallSession?.data?.call_sessionable?.business
        ?.business_legal_name ?? null
    );
  }
  const call = callStore.currentCall;
  const rawCall = call ? toRaw(call) : null;
  return rawCall?.customParameters?.get("callerBusinessName") ?? null;
});

const partnerName = computed(() => {
  if (isOutgoing.value) {
    return callStore.activeCallSession?.partner ?? null;
  }
  const call = callStore.currentCall;
  const rawCall = call ? toRaw(call) : null;
  return rawCall?.customParameters?.get("callerIntegrationPartner") ?? null;
});

const activeUserPhoneNumber = computed(() => {
  if (isOutgoing.value) {
    return (
      callStore.activeCallSession?.data?.from ??
      savedPhoneNumber.value?.phone_number ??
      PhoneSelectionType.Auto
    );
  }
  const call = callStore.currentCall;
  const rawCall = call ? toRaw(call) : null;
  return (
    rawCall?.customParameters?.get("to") ?? rawCall?.parameters?.To ?? null
  );
});

const dealLink = computed(() => {
  if (isOutgoing.value) {
    return undefined;
  }
  const call = callStore.currentCall;
  const rawCall = call ? toRaw(call) : null;
  const applicationId = rawCall?.customParameters?.get("applicationId");
  if (!applicationId) {
    return undefined;
  }
  return {
    name: ROUTE_DEAL_PROGRESS,
    params: { id: applicationId },
  };
});

const infoRows = computed(() => {
  const sections = [
    {
      name: "callee-name",
      iconOptions: {
        icon: IconProfileSmall,
        viewBox: "0 0 16 16",
      },
      value: contactName.value,
    },
    {
      name: "business-name",
      iconOptions: {
        icon: IconDeals,
        viewBox: "10 10 20 20",
      },
      value: businessName.value,
      link: dealLink.value,
    },
    {
      name: "partner-name",
      iconOptions: {
        icon: IconPersons,
        viewBox: "10 10 20 20",
      },
      value: partnerName.value,
    },
  ];
  return sections.filter((section) => !!section.value);
});

const getRouteHref = (route: RouteLocationRaw): string => {
  return router.resolve(route).href;
};

const acceptCall = async () => {
  const incoming = callStore.incomingCall;
  const device = callStore.device;

  if (!incoming || !device) {
    return;
  }

  callStore.stopIncomingSound();

  try {
    const call = await toRaw(device).connect({
      connectToken: toRaw(incoming).connectToken,
    });

    // Clear incoming, set active
    callStore.setActiveCall(call);

    // Register call listeners
    call.on(TwilioCallActions.Disconnect, handleCallEnded);
    call.on(TwilioCallActions.Cancel, handleCallEnded);
    call.on(TwilioCallActions.Mute, (isMuted: boolean) => {
      callStore.isMicrophoneMuted = isMuted;
    });

    // Show call notes modal for incoming calls
    showCallNotesModal(call.customParameters);
  } catch (error) {
    showMessage(t("ERRORS.TWILIO.FAILED_TO_ACCEPT_CALL"), "error");
    callStore.clearCallState();
  }
};

/**
 * Set up the call dialog to show the notes modal after accepting an incoming call
 */
const showCallNotesModal = (customParameters: Map<string, string>) => {
  const callSessionId = customParameters.get("callSessionId");
  if (!callSessionId) {
    return;
  }

  const callerIntegrationPartner =
    customParameters.get("callerIntegrationPartner") ?? "";
  const callerBusinessName = customParameters.get("callerBusinessName") ?? "";

  callDialog.value = {
    id: Number(callSessionId),
    type: CommunicationType.call,
    data: {
      id: Number(callSessionId),
      session_id: callSessionId,
      status: CallSessionStatus.InProgress,
      call_disposition: "",
      call_sessionable: {
        business: {
          business_legal_name: callerBusinessName,
        },
        client_name: callerIntegrationPartner,
      },
      is_outgoing: false,
    },
  };
};

const rejectCall = () => {
  const incoming = callStore.incomingCall;
  if (incoming) {
    toRaw(incoming).reject();
  }
  callStore.clearCallState();
  // Notify other tabs that the call ended
  callStore.notifyCallEnded();
};

const disconnectCall = () => {
  const call = callStore.activeCall;
  const device = callStore.device;

  if (call) {
    toRaw(call).disconnect();
  }
  if (device && toRaw(device).isBusy) {
    toRaw(device).calls?.forEach((c) => c.disconnect());
  }
  callStore.clearCallState();
  callStore.notifyCallEnded();
};

const toggleMute = () => {
  const call = callStore.activeCall;
  if (call) {
    toRaw(call).mute(!callStore.isMicrophoneMuted);
  }
};

const toggleSilence = () => {
  const isSilenced = isRingtoneSilenced.value;
  if (isSilenced) {
    // Un-silence: play sound again
    callStore.playIncomingSound();
    callStore.isRingtoneSilenced = false;
    isRingtoneSilenced.value = false;
  } else {
    // Silence: stop the sound but don't call ignore() so we can still reject
    callStore.stopIncomingSound();
    callStore.isRingtoneSilenced = true;
    isRingtoneSilenced.value = true;
    // Notify other tabs to also silence
    callStore.notifyCallSilenced();
  }
};

const handleCallEnded = () => {
  callStore.clearCallState();
};

const authorizeUseMedia = async () => {
  try {
    const permissions = await navigator.permissions.query({
      name: "microphone" as PermissionName,
    });
    if (permissions?.state !== "granted") {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => {
        track.stop();
      });
    }
    await navigator.mediaDevices
      .enumerateDevices()
      .then((devices) => (audioDevices.value = devices));
  } catch (err) {
    showMessage(t("TWILIO.DEVICE_AUTHORIZATION_ERROR"), "error");
    Sentry.captureException(err);
  }
};

const setDevice = (selectedDevice: string, type: "input" | "output") => {
  const device = callStore.device;
  if (!device) {
    return;
  }
  if (type === "input") {
    toRaw(device).audio?.setInputDevice(selectedDevice);
    return;
  }
  toRaw(device).audio?.speakerDevices?.set(selectedDevice);
};

onMounted(async () => {
  await authorizeUseMedia();
});
// Note: Do NOT clear call state on unmount - the call should continue
// even if PiP is closed. Call state is managed by TwilioPiPManager.
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.call-session-info-row {
  @apply flex h-[23px] items-center justify-start space-x-2;

  .icon {
    @apply flex-none text-label;
  }
}

.dialer-round-button {
  @apply flex size-10 cursor-pointer items-center justify-center rounded-full;
}

.twilio-dialer-actions {
  @apply mt-auto flex w-full items-center justify-center space-x-3 p-4;
}

.twilio-pip-dialer {
  font-family:
    system-ui,
    -apple-system,
    sans-serif;
  min-height: 100%;
}
</style>
